import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Users, Home, TreePine, Wallet, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const QUESTIONS = [
  { key: "zona", q: "Dove si trova?", options: ["Corato centro", "Corato periferia", "Fuori Corato"] },
  { key: "tipo", q: "Che tipo di immobile è?", options: ["Appartamento", "Villa / Villetta", "Attico", "Casa indipendente"] },
  { key: "plus", q: "Qual è la sua caratteristica principale?", options: ["Spazio esterno", "Box / Garage", "Ristrutturato", "Molto luminoso"] },
];

const PROFILES = [
  { icon: Users, title: "Coppia", need: "cerca trilocale con box", tag: "Ricerca attiva" },
  { icon: TreePine, title: "Famiglia", need: "cerca spazio esterno", tag: "Priorità giardino" },
  { icon: Wallet, title: "Acquirente", need: "mutuo già verificato", tag: "Budget confermato" },
];

export default function BuyerDemo({ onVerify }) {
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("form"); // form | searching | results
  const answered = Object.keys(answers).length;

  const search = () => {
    setPhase("searching");
    setTimeout(() => setPhase("results"), 1900);
  };

  return (
    <section id="acquirenti" className="relative py-24 sm:py-32 bg-accent-subtle">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Intro */}
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Domanda già presente</Overline>
              <h2 className="font-display font-semibold text-anthracite text-5xl sm:text-6xl leading-[0.95] tracking-tight mt-5">
                E se il tuo acquirente fosse già tra noi?
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-anthracite/70 max-w-md">
                Ogni giorno raccogliamo richieste di persone che cercano casa. Inserisci poche
                informazioni e verifica se il tuo immobile corrisponde alle esigenze dei nostri clienti.
              </p>
            </Reveal>
          </div>

          {/* Interactive panel */}
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="rounded-3xl bg-white border border-border-subtle p-6 sm:p-9 shadow-[0_25px_60px_-30px_rgba(15,92,74,0.3)]">
                <AnimatePresence mode="wait">
                  {phase !== "results" && (
                    <motion.div key="form" exit={{ opacity: 0, y: -10 }} className="space-y-7">
                      {QUESTIONS.map((item, idx) => (
                        <div key={item.key}>
                          <div className="flex items-center gap-3 mb-3">
                            <span className="grid place-items-center h-7 w-7 rounded-full bg-brand/10 text-brand text-sm font-semibold">
                              {idx + 1}
                            </span>
                            <span className="font-semibold text-anthracite">{item.q}</span>
                          </div>
                          <div className="flex flex-wrap gap-2.5">
                            {item.options.map((opt) => {
                              const active = answers[item.key] === opt;
                              return (
                                <button
                                  key={opt}
                                  onClick={() => setAnswers((a) => ({ ...a, [item.key]: opt }))}
                                  className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${
                                    active
                                      ? "bg-brand text-white border-brand"
                                      : "bg-warm text-anthracite/70 border-border-subtle hover:border-brand hover:text-brand"
                                  }`}
                                  data-testid={`buyer-opt-${item.key}-${opt}`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      <button
                        onClick={search}
                        disabled={answered < 3 || phase === "searching"}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold py-4 transition-all hover:bg-brand-hover hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100"
                        data-testid="buyer-search-cta"
                      >
                        {phase === "searching" ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Ricerca in corso...</>
                        ) : (
                          <><Search className="h-4 w-4" /> Cerca un possibile acquirente</>
                        )}
                      </button>
                      {answered < 3 && (
                        <p className="text-center text-xs text-anthracite/45 -mt-3">
                          Rispondi alle tre domande per avviare la ricerca
                        </p>
                      )}
                    </motion.div>
                  )}

                  {phase === "results" && (
                    <motion.div
                      key="results"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-4"
                      data-testid="buyer-results"
                    >
                      <div className="flex items-center gap-2 text-brand font-semibold">
                        <Sparkles className="h-4 w-4" />
                        <span className="text-sm uppercase tracking-[0.18em]">Profili dimostrativi compatibili</span>
                      </div>
                      {PROFILES.map((p, i) => (
                        <motion.div
                          key={p.title}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                          className="flex items-center gap-4 rounded-2xl bg-warm border border-border-subtle p-4"
                        >
                          <span className="grid place-items-center h-12 w-12 shrink-0 rounded-xl bg-brand/10 text-brand">
                            <p.icon className="h-5 w-5" />
                          </span>
                          <span className="flex-1 leading-tight">
                            <span className="block font-semibold text-anthracite">{p.title}</span>
                            <span className="block text-sm text-anthracite/60">{p.need}</span>
                          </span>
                          <span className="hidden sm:inline text-xs font-semibold text-brand bg-brand/10 rounded-full px-3 py-1.5">
                            {p.tag}
                          </span>
                        </motion.div>
                      ))}

                      <button
                        onClick={onVerify}
                        className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold py-4 mt-2 transition-all hover:bg-brand-hover hover:scale-[1.01]"
                        data-testid="buyer-verify-cta"
                      >
                        Verifica la compatibilità
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </button>
                      <button
                        onClick={() => { setPhase("form"); }}
                        className="w-full text-center text-sm text-anthracite/50 hover:text-brand transition-colors"
                        data-testid="buyer-reset"
                      >
                        Modifica la ricerca
                      </button>
                      <p className="text-center text-[11px] text-anthracite/40 flex items-center justify-center gap-1.5">
                        <Home className="h-3 w-3" /> Profili a scopo dimostrativo, non rappresentano clienti reali.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
